import { useEffect, useRef, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Message } from '@/components/Message';
import { ChatInput } from '@/components/ChatInput';
import { useChatStore } from '@/store';
import { chatAPI } from '@/services/api';
import { generateWelcomeMessage } from '@/services/chatHandler';
import { useTaskRecovery } from '@/hooks/useTaskRecovery';
import {
  formatFindSimilarUsersResponse,
  formatSimilarUsersHotResponse,
  formatExtractLeadsResponse,
  formatAnalyzeLeadsResponse,
} from '@/utils/taskFormatter';

import type { ChatMessage } from '@/types';

export function ChatPage() {
  const {
    messages,
    isLoading,
    currentSessionId,
    createSession,
    addMessage,
    setLoading,
  } = useChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 页面挂载时恢复未完成的异步任务
  useTaskRecovery();

  // 页面挂载时重置 isLoading，避免刷新后输入框被禁用
  useEffect(() => {
    setLoading(false);
  }, [setLoading]);

  // 自动滚动到底部
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // 初始化会话
  useEffect(() => {
    if (!currentSessionId) {
      createSession();
      // 添加欢迎消息
      setTimeout(() => {
        addMessage({
          role: 'assistant',
          content: generateWelcomeMessage(),
          type: 'text',
        });
      }, 100);
    }
  }, [currentSessionId, createSession, addMessage]);

  // 处理用户消息
  const handleSendMessage = async (content: string) => {
    // 添加用户消息
    addMessage({
      role: 'user',
      content,
      type: 'text',
    });

    setLoading(true);

    // 添加加载状态消息
    addMessage({
      role: 'assistant',
      content: '',
      type: 'loading',
    });

    try {
      // 获取对话上下文（最近的5条消息）
      const context = messages.slice(-5).map(m => ({
        role: m.role,
        content: m.content,
      }));

      // 调用统一的聊天接口
      const response = await chatAPI.sendMessage(content, context);
      
      if (response.success && response.data) {
        const { updateLastMessage } = useChatStore.getState();
        const finalType = (response.data.type as ChatMessage['type']) || 'text';
        updateLastMessage(response.data.message, response.data.data, finalType);
      } else {
        const { updateLastMessage } = useChatStore.getState();
        updateLastMessage(`❌ ${response.message || '处理失败'}`, undefined, 'error');
      }
    } catch (error) {
      const { updateLastMessage } = useChatStore.getState();
      updateLastMessage(`❌ 出错了：${error instanceof Error ? error.message : '未知错误'}`, undefined, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 处理快捷操作（4个按钮）
  const handleQuickAction = (action: string, data: any) => {
    // 添加用户操作消息
    const userContent = action === '评论提炼' || action === '评论分析' 
      ? `【${action}】已输入目标视频链接`
      : `【${action}】`;
    
    addMessage({
      role: 'user',
      content: userContent,
      type: 'text',
    });

    // 根据操作类型格式化响应内容
    let content = '';
    switch (action) {
      case '获取同类':
        content = formatFindSimilarUsersResponse(data);
        break;
      case '同类热门':
        content = formatSimilarUsersHotResponse(data);
        break;
      case '评论提炼':
        content = formatExtractLeadsResponse(data);
        break;
      case '评论分析':
        content = formatAnalyzeLeadsResponse(data);
        break;
      default:
        content = '操作完成';
    }

    // 精简数据，避免超出 localStorage 配额
    const trimmedData = {
      action,
      timestamp: Date.now(),
      summary: content.substring(0, 1000),
    };

    // 添加助手响应消息（不存储完整原始数据）
    addMessage({
      role: 'assistant',
      content,
      type: 'analysis',
      data: trimmedData,
    });
  };

  // 格式化函数已提取到 @/utils/taskFormatter，直接复用

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 flex flex-col min-w-0">
        {/* 消息列表 */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <div className="text-4xl mb-4">👋</div>
                <p>开始一个新的对话</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* 输入�?*/}
        <ChatInput 
          onSend={handleSendMessage} 
          isLoading={isLoading}
          onQuickAction={handleQuickAction}
        />
      </main>
    </div>
  );
}

